import { useEffect, useState } from 'react';

import {
    convertAbnAndNameLookup,
    ILookupName,
    lookupAbn,
    lookupBusinessName,
} from '@/apiCalls/businessLookup';
import { Combobox, IComboboxOption } from '@/components/Combobox';
import { Input } from '@/components/Input';
import {
    selectCompanyNumber,
    selectEmployerLookupDetails,
    setCompanyNumber,
    setEmployerLookupDetails,
    setEmployerName,
} from '@/context/borrower';
import { selectIsNZ } from '@/context/display';
import { useAppDispatch, useAppSelector } from '@/context/storeHooks';

export interface IProps {
    baseId: string;
    showErrors: boolean;
}

export const testId = 'EmployerLookup';

export const EmployerLookup = ({ showErrors, baseId }: IProps): JSX.Element => {
    // Redux state
    const employerDetails = useAppSelector(selectEmployerLookupDetails);
    const companyNumber = useAppSelector(selectCompanyNumber);
    const isNZ = useAppSelector(selectIsNZ);
    const dispatch = useAppDispatch();

    const heading = 'Employer Name';

    // Local state
    const [query, setQuery] = useState<undefined | string>(
        employerDetails?.name
    );
    const [options, setOptions] = useState<IComboboxOption[]>([]);
    const [data, setData] = useState<ILookupName[]>([]);
    const [loading, setLoading] = useState<boolean>(false);

    function handleChange(event: React.ChangeEvent<HTMLInputElement>): void {
        const { value } = event.target;
        setQuery(value);

        // For NZ users, if they type a company name manually (not from dropdown),
        // allow them to continue without requiring a selection
        if (isNZ && value && value.trim().length > 0) {
            // Clear any existing employer details to allow manual entry
            if (!data.some((item) => item.Name === value)) {
                dispatch(setEmployerName(value));
                dispatch(
                    setEmployerLookupDetails({
                        name: value,
                        abn: '',
                        companyNumber: '',
                        addressText: '',
                    } as any)
                );
            }
        }
    }

    // Debounced search for employer by name or by direct NZBN/ABN
    useEffect(() => {
        setLoading(true);
        const delayDebounceFn = setTimeout(async () => {
            try {
                const newOptions: IComboboxOption[] = [];

                // Early exit to avoid empty/short queries
                if (query === undefined || query.trim().length < 2) {
                    setOptions([]);
                    setData([]);
                    setLoading(false);
                    return;
                }

                // Direct numeric lookup:
                // AU: exactly 11 digits (ABN)
                if (!isNZ && /^\d{11}$/.test(query)) {
                    const r = await lookupAbn(query);
                    if (!r) {
                        // For AU, if ABN not found, still allow manual entry
                        setOptions([]);
                        setData([]);
                        setLoading(false);
                        return;
                    }

                    newOptions.push({ id: 0, value: r.EntityName || r.Abn });
                    setData([
                        {
                            Abn: r.Abn,
                            AbnStatus: r.AbnStatus || 'Active',
                            isCurrent: true,
                            Name: r.EntityName || r.Abn,
                            NameType: 'Business Name',
                            Postcode: r.AddressPostcode || '',
                            Score: 100,
                            State: r.AddressState || '',
                        },
                    ]);

                    setOptions(newOptions);
                    setLoading(false);
                    return;
                }

                // NZ: exactly 13 digits (NZBN)
                if (isNZ && /^\d{13}$/.test(query)) {
                    const r = await lookupAbn(query);

                    console.log('result employer lookup', r);
                    if (!r) {
                        // For NZ, if NZBN not found, still allow manual entry
                        // Show a hint that user should enter Company Number
                        setOptions([]);
                        setData([]);
                        setLoading(false);
                        return;
                    }

                    newOptions.push({ id: 0, value: r.EntityName || r.Abn });
                    setData([
                        {
                            Abn: r.Abn,
                            AbnStatus: r.AbnStatus || 'Active',
                            isCurrent: true,
                            Name: r.EntityName || r.Abn,
                            NameType: 'Business Name',
                            Postcode: r.AddressPostcode || '',
                            Score: 100,
                            State: r.AddressState || '',
                        },
                    ]);

                    setOptions(newOptions);
                    setLoading(false);
                    return;
                }

                // Name-based lookup (AU/NZ). Always guard to an array.
                let resp: ILookupName[] | undefined;
                try {
                    resp = await lookupBusinessName(query);
                } catch {
                    resp = [];
                }
                const lookup = Array.isArray(resp) ? resp : [];

                lookup.forEach((item, index) => {
                    const tail = item.Abn ? ` • ${item.Abn.slice(-4)}` : '';
                    newOptions.push({
                        id: index,
                        value: `${item.Name}${tail}`,
                    });
                });

                setOptions(newOptions);
                setData(lookup);
                setLoading(false);
            } catch {
                setOptions([]);
                setData([]);
                setLoading(false);
            }
        }, 1000);

        return () => clearTimeout(delayDebounceFn);
    }, [query, isNZ]);

    // On select: fetch detail, populate Employer Name + Address, and set Company Number for NZ
    async function handleSelect(option: IComboboxOption): Promise<void> {
        const nameLookupResult = data?.[option.id];
        if (!nameLookupResult) return;

        const d = await lookupAbn(nameLookupResult.Abn);
        if (!d) return;

        // Use existing converter for your domain structure
        const employer = convertAbnAndNameLookup(nameLookupResult, d);

        // Fallback formatted address if street components are missing
        const fallbackAddress = [
            (employer as any)?.streetNumber,
            (employer as any)?.streetName,
            (employer as any)?.suburbName,
            (employer as any)?.stateName ?? d.AddressState,
            (employer as any)?.postCode ?? d.AddressPostcode,
        ]
            .filter(Boolean)
            .join(' ')
            .replace(/\s+/g, ' ')
            .trim();

        // Write Employer Name + details into Redux
        dispatch(
            setEmployerName((employer as any)?.name || nameLookupResult.Name)
        );
        dispatch(
            setEmployerLookupDetails({
                ...(employer as any),
                name: (employer as any)?.name || nameLookupResult.Name,
                abn: d.Abn,
                // Ensure these exist so the address display string can be built
                stateName: (employer as any)?.stateName ?? d.AddressState ?? '',
                postCode:
                    (employer as any)?.postCode ?? d.AddressPostcode ?? '',
                streetNumber: (employer as any)?.streetNumber ?? '',
                streetName: (employer as any)?.streetName ?? '',
                suburbName: (employer as any)?.suburbName ?? '',
                addressText: fallbackAddress,
            } as any)
        );

        // NZ: populate Company Number when present
        const companyNo = (employer as any)?.companyNumber || d.CompanyNumber;
        if (isNZ && companyNo) {
            dispatch(setCompanyNumber(companyNo));
        }
    }

    // Tooltip (make it country-aware)
    const tooltip = isNZ
        ? `Search for your employer by name or NZBN. If not found, enter company name manually and fill Company Number below.`
        : `Search for and select your employer by name or ABN.`;

    // Address string shown in the disabled input
    const employerAddress = employerDetails
        ? `${employerDetails?.streetNumber ?? ''} ${
              employerDetails?.streetName ?? ''
          } ${employerDetails?.suburbName ?? ''} ${
              employerDetails?.stateName ?? ''
          } ${employerDetails?.postCode ?? ''}`
              .replace(/\s+/g, ' ')
              .trim()
        : '';

    return (
        <div data-testid={testId}>
            <Combobox
                options={options}
                loading={loading}
                placeholder={isNZ ? 'Enter employer name or NZBN' : 'Required'}
                heading={heading}
                onChange={handleChange}
                query={query ?? employerDetails?.name ?? ''}
                onSelect={handleSelect}
                showErrors={showErrors && !employerDetails?.name}
                baseId={baseId}
                tooltipContent={tooltip}
            />

            {isNZ && (
                <>
                    {employerDetails?.name && !employerDetails?.abn && (
                        <p className='text-sm text-gray-600 mb-2'>
                            Company not found. Please enter Company Number
                            below.
                        </p>
                    )}
                    <div className='_flex space-x-2 flex-center'>
                        <div style={{ flex: 1 }}>
                            <Input
                                title='Company Number'
                                placeholder='Enter Company Number'
                                showErrors={false}
                                id={`${baseId}-input-employerCompanyNumber`}
                                value={
                                    companyNumber ??
                                    employerDetails?.companyNumber ??
                                    employerDetails?.abn ??
                                    ''
                                }
                                onChange={(val): void => {
                                    if (typeof val === 'boolean') return;
                                    // Keep Company Number also in the employer details slice
                                    dispatch(setCompanyNumber(val));
                                    dispatch(
                                        setEmployerLookupDetails({
                                            ...(employerDetails || {}),
                                            companyNumber: val,
                                        } as any)
                                    );
                                }}
                            />
                        </div>
                        <div style={{ flex: 2 }}>
                            <Input
                                title='Employer Address'
                                placeholder={
                                    employerDetails?.abn
                                        ? 'Auto-populated from NZBN'
                                        : 'N/A'
                                }
                                showErrors={false}
                                id={`${baseId}-input-employerAddress`}
                                disabled={true}
                                value={employerAddress || 'N/A'}
                            />
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};
